import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Globe, ArrowRight, Briefcase, User } from 'lucide-react';

const Login = () => {
  const [emailStaff, setEmailStaff] = useState('');
  const [passwordStaff, setPasswordStaff] = useState('');
  const [emailClient, setEmailClient] = useState('');
  const [passwordClient, setPasswordClient] = useState('');
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoadingStaff(true);
    try {
      await login(emailStaff, passwordStaff);
      toast.success(t('success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleClientLogin = async (e) => {
    e.preventDefault();
    setLoadingClient(true);
    try {
      await login(emailClient, passwordClient);
      toast.success(t('success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoadingClient(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-red-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLanguage}
          className="gap-2"
          data-testid="language-toggle-login"
        >
          <Globe className="h-4 w-4" />
          {i18n.language.toUpperCase()}
        </Button>
      </div>

      {/* Logo en haut */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <img
          src="/logo.png"
          alt="LCS Case Management"
          className="h-20 w-auto"
        />
      </div>

      {/* Deux cartes côte à côte */}
      <div className="w-full max-w-6xl mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* CARTE EMPLOYÉ / ADMINISTRATEUR */}
        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-xl hover:shadow-3xl transition-shadow">
          <CardHeader className="space-y-3 text-center pb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
            <div className="flex justify-center mb-2">
              <div className="p-4 bg-white/20 rounded-full">
                <Briefcase className="h-10 w-10" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              Connexion Employé
            </CardTitle>
            <CardDescription className="text-blue-100">
              Administrateur & Employé
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleStaffLogin} className="space-y-5" data-testid="staff-login-form">
              <div className="space-y-2">
                <Label htmlFor="email-staff">Courriel</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email-staff"
                    type="email"
                    value={emailStaff}
                    onChange={(e) => setEmailStaff(e.target.value)}
                    placeholder="employe@lcsimmigration.com"
                    className="pl-10 h-12"
                    required
                    data-testid="email-staff-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password-staff">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password-staff"
                    type="password"
                    value={passwordStaff}
                    onChange={(e) => setPasswordStaff(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-12"
                    required
                    data-testid="password-staff-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg"
                disabled={loadingStaff}
                data-testid="staff-login-submit-btn"
              >
                {loadingStaff ? 'Connexion...' : 'Se connecter'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* CARTE CLIENT */}
        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-xl hover:shadow-3xl transition-shadow">
          <CardHeader className="space-y-3 text-center pb-6 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-t-lg">
            <div className="flex justify-center mb-2">
              <div className="p-4 bg-white/20 rounded-full">
                <User className="h-10 w-10" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              Connexion Client
            </CardTitle>
            <CardDescription className="text-red-100">
              Espace Client
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleClientLogin} className="space-y-5" data-testid="client-login-form">
              <div className="space-y-2">
                <Label htmlFor="email-client">Courriel</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email-client"
                    type="email"
                    value={emailClient}
                    onChange={(e) => setEmailClient(e.target.value)}
                    placeholder="client@email.com"
                    className="pl-10 h-12"
                    required
                    data-testid="email-client-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password-client">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password-client"
                    type="password"
                    value={passwordClient}
                    onChange={(e) => setPasswordClient(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-12"
                    required
                    data-testid="password-client-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-lg"
                disabled={loadingClient}
                data-testid="client-login-submit-btn"
              >
                {loadingClient ? 'Connexion...' : 'Se connecter'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex justify-center pb-6">
            <p className="text-sm text-gray-600">
              Pas de compte?{' '}
              <Link to="/register" className="text-red-600 hover:text-red-700 font-semibold" data-testid="register-link">
                S'inscrire
              </Link>
            </p>
          </CardFooter>
        </Card>

      </div>
    </div>
  );
};

export default Login;